import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    Divider,
    Button,
    Alert,
    IconButton,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Close, FileUploadOutlined } from '@mui/icons-material';
import { uploadScormFile } from '../../apis/scormUpload';
import { addScormFile } from '../../store/slices/scormFilesSlice';
import Loader from '../loader/Loader';
import { useSubscription } from '../../hooks/useSubscription';

const ALLOWED_MIME = ['application/zip', 'application/x-zip-compressed'];

const UploadScormDialog = ({ openUpload, onClose, onUploadSuccess }) => {
    // const [progress, setProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    // const [uploadStartTime, setUploadStartTime] = useState(null);
    // const [uploadSpeed, setUploadSpeed] = useState(null);
    const [showQuotaDialog, setShowQuotaDialog] = useState(false);
    const fileInputRef = useRef();
    const dispatch = useDispatch();
    const { quota } = useSubscription();

    // Calculate effective max file size in MB (considering both max_package_size and remaining space)
    const effectiveMaxMB = useMemo(() => {
        if (!quota) {
            return 500; // Default to 500MB
        }
        
        // Calculate remaining space (total - used) in MB
        const remainingSpaceMB = quota.upload_quota && quota.storage_consumed_in_mb !== undefined
            ? Math.max(0, quota.upload_quota - quota.storage_consumed_in_mb)
            : null;
        
        // Get max package size in MB
        const maxPackageSizeMB = quota.max_package_size || null;
        
        // Use the minimum of max_package_size and remaining space
        // If either is null, use the available one
        if (maxPackageSizeMB !== null && remainingSpaceMB !== null) {
            return Math.min(maxPackageSizeMB, remainingSpaceMB);
        } else if (maxPackageSizeMB !== null) {
            return maxPackageSizeMB;
        } else if (remainingSpaceMB !== null) {
            return remainingSpaceMB;
        } else {
            return 500; // Default to 500MB
        }
    }, [quota?.max_package_size, quota?.upload_quota, quota?.storage_consumed_in_mb]);

    // Get max file size in bytes
    const maxFileSizeBytes = useMemo(() => {
        return effectiveMaxMB * 1024 * 1024; // Convert MB to bytes
    }, [effectiveMaxMB]);

    // Validation schema
    const validationSchema = useMemo(() => {
        return Yup.object({
            file: Yup.mixed()
                .nullable()
                .required('Please select a SCORM .zip file to upload.')
                .test('fileType', 'Invalid file type. Only ZIP files are allowed.', (value) => {
                    if (!value) return false; // If no file, fail (required already checked, but double-check)
                    // Check by file extension first (more reliable)
                    const fileName = value.name || '';
                    if (fileName.toLowerCase().endsWith('.zip')) return true;
                    // Check by MIME type as secondary check
                    if (value.type && ALLOWED_MIME.includes(value.type)) return true;
                    return false;
                })
                .test('fileSize', `The file size must be less than ${effectiveMaxMB}MB.`, (value) => {
                    if (!value) return false; // If no file, fail (required already checked)
                    return parseInt(value.size) <= parseInt(maxFileSizeBytes);
                }),
        });
    }, [effectiveMaxMB, maxFileSizeBytes]);

    const formik = useFormik({
        initialValues: {
            file: null,
        },
        validationSchema,
        onSubmit: async (values) => {
            if (!values.file) {
                toast.error('Please select a SCORM .zip file to upload.');
                return;
            }
            setUploading(true);
            try {
                const uploadedFile = await uploadScormFile(values.file, () => {}, false);

                setUploading(false);
                toast.success('Upload successful!');

                if (uploadedFile) {
                    dispatch(addScormFile(uploadedFile.scorm_file));
                    formik.resetForm();
                    if (onUploadSuccess) onUploadSuccess(uploadedFile.scorm_file);
                    onClose();
                }
            } catch (err) {
                setUploading(false);
                const errorCode = err?.response?.data?.code;
                const quotaErrorCodes = [432, 433, 434, 438, 439, 440];
                const showDialogue = errorCode && quotaErrorCodes.includes(errorCode);
                if (showDialogue) {
                    setShowQuotaDialog(true);
                } else {
                    toast.error(err?.response?.data?.error || err?.message || 'Upload failed. Please try again.');
                }
            }
        },
    });

    // Reset form when dialog closes
    useEffect(() => {
        if (!openUpload) {
            formik.resetForm();
        }
    }, [openUpload]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            // Set the file value and validate
            formik.setFieldValue('file', selectedFile);
            // Validate after a short delay to ensure the value is set
            setTimeout(() => {
                formik.validateField('file');
            }, 0);
            formik.setFieldTouched('file', true);
        } else {
            formik.setFieldValue('file', null);
        }
        e.target.value = ''; // reset input so same file can be chosen again
    };

    const handleDrop = (e) => {
        e.preventDefault();
        if (uploading) return;

        const selectedFile = e.dataTransfer.files?.[0];
        if (selectedFile) {
            // Set the file value and validate
            formik.setFieldValue('file', selectedFile);
            // Validate after a short delay to ensure the value is set
            setTimeout(() => {
                formik.validateField('file');
            }, 0);
            formik.setFieldTouched('file', true);
        } else {
            formik.setFieldValue('file', null);
        }
    };

    const handleDragOver = (e) => e.preventDefault();

    const handleBrowseClick = () => {
        if (!uploading) fileInputRef.current.click();
    };


    const formatSize = useCallback((size) => {
        if (!size) return '';
        if (size > 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
        if (size > 1024) return `${(size / 1024).toFixed(1)} KB`;
        return `${size} B`;
    }, []);


    const handleClose = () => {
        formik.resetForm();
        fileInputRef.current = null;
        setUploading(false);
        setShowQuotaDialog(false);
        onClose();
    };

    const handleConfirm = async () => {
        setShowQuotaDialog(false);

        if (!formik.values.file) {
            toast.error('Please select a SCORM .zip file to upload.');
            return;
        }
        setUploading(true);
        try {
            const uploadedFile = await uploadScormFile(formik.values.file, () => {}, true);

            setUploading(false);
            toast.success('Upload successful!');

            if (uploadedFile) {
                dispatch(addScormFile(uploadedFile.scorm_file));
                formik.resetForm();
                if (onUploadSuccess) onUploadSuccess(uploadedFile.scorm_file);
                onClose();
            }
        } catch (err) {
            setUploading(false);
            toast.error(err?.response?.data?.error || err?.message || 'Upload failed. Please try again.');
        }
    };

    return (
        <>
        <Dialog
            open={openUpload}
            onClose={uploading ? undefined : handleClose}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={uploading}
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 3,
                    padding: '.75rem 1.5rem',
                },
            }}
            className="file-upload-dialog"
        >
            <DialogTitle className="title">
                Upload Course
                <IconButton onClick={handleClose} disabled={uploading}>
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent className="content-container">
                <Loader isLoading={uploading} text="Uploading..." />

                {/* Instructions */}
                <Typography variant="subtitle1" className="instruction-heading">
                    Instructions:
                </Typography>
                <Typography variant="body2" className="instruction-text mb-0.5">
                    1. File must be less than <b>{effectiveMaxMB}MB</b>.
                </Typography>
                <Typography variant="body2" className="instruction-text mb-0.5">
                    2. It must be a SCORM <b>.zip</b> file.
                </Typography>
                <Typography variant="body2" className="instruction-text mb-2">
                    3. It must be a valid SCORM package containing a <b>manifest</b> file.
                </Typography>

                {/* Upload Box */}
                <Box
                    className="upload-container"
                    sx={{
                        cursor: uploading ? 'not-allowed' : 'pointer',
                        '&:hover': { backgroundColor: uploading ? '#F8F7F9' : '#f9fafb' },
                    }}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={handleBrowseClick}
                >
                    <FileUploadOutlined className="upload-icon" fontSize="large" />
                    <Typography variant="body2">Click to upload or drag and drop file</Typography>
                    <input
                        type="file"
                        className="d-none"
                        accept=".zip"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        disabled={uploading}
                    />
                </Box>

                {/* File info */}
                {formik.values.file && (
                    <Box mt={3} display="flex" alignItems="center" gap={2}>
                        <Box width={50}>
                            <svg width="32" height="32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="32" height="32" rx="6" fill="#e9ecf3" />
                                <text
                                    x="16"
                                    y="22"
                                    textAnchor="middle"
                                    fontSize="10"
                                    fill="#7a88a9"
                                    fontFamily="Arial"
                                    fontWeight="bold"
                                >
                                    ZIP
                                </text>
                            </svg>
                        </Box>
                        <Box flexGrow={1}>
                            <Typography>{formik.values.file.name}</Typography>
                            <Typography variant="body2" color="secondary">
                                {formatSize(formik.values.file.size)}
                            </Typography>
                        </Box>
                    </Box>
                )}
                {formik.touched.file && formik.errors.file && (
                    <Alert severity="error" className="mt-2">
                        {formik.errors.file}
                    </Alert>
                )}
            </DialogContent>

            <DialogActions className="action-container">
                <Button variant="outlined" color="secondary" onClick={handleClose} disabled={uploading}>
                    Cancel
                </Button>
                <Button 
                    variant="contained" 
                    onClick={formik.handleSubmit} 
                    disabled={uploading || !formik.values.file || (formik.touched.file && Boolean(formik.errors.file))}
                >
                    {uploading ? 'Uploading...' : 'Upload'}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Quota Warning Dialog */}
        <Dialog
            open={showQuotaDialog}
            onClose={() => setShowQuotaDialog(false)}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle className="title">
                Quota Warning
                <IconButton onClick={() => setShowQuotaDialog(false)}>
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Typography variant="body1">
                    Your ongoing file processing could exhaust your quota. Additional uploads might fail if you hit the limit.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" color="secondary" onClick={() => setShowQuotaDialog(false)}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleConfirm}>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    </>
    );
};

export default UploadScormDialog;
